import { apiFetch } from "@/lib/api-client";
import type { LessonProgress } from "./types";

const path = (p: string) => `/api/academy${p}`;

export const fetchProgress = (token: string): Promise<{ progress: LessonProgress[] }> =>
  apiFetch(path("/progress"), { token });

export const startLesson = (
  token: string,
  lessonId: string,
): Promise<{ status: string; lessonId: string }> =>
  apiFetch(path(`/lessons/${lessonId}/start`), { method: "POST", token });

export const updateStage = (
  token: string,
  lessonId: string,
  stage: number,
): Promise<{ status: string; lastStage: number }> =>
  apiFetch(path(`/lessons/${lessonId}/stage`), { method: "POST", token, body: { stage } });

export const completeLesson = (
  token: string,
  lessonId: string,
  quizAnswer: number,
  quizScore: number,
): Promise<{ status: string; lessonId: string; completed: boolean }> =>
  apiFetch(path(`/lessons/${lessonId}/complete`), {
    method: "POST",
    token,
    body: { quizAnswer, quizScore },
  });

// ── XP & Streaks ──

export interface XpData {
  totalXp: number;
  weeklyXp: number;
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
  streakFreezeAvailable: boolean;
  reviewsDue: number;
  badges: string[];
}

export const fetchXp = (token: string): Promise<XpData> => apiFetch(path("/xp"), { token });

export const addXp = (
  token: string,
  amount: number,
  source: string,
): Promise<{ totalXp: number; weeklyXp: number; currentStreak: number; newBadges: string[] }> =>
  apiFetch(path("/xp/add"), { method: "POST", token, body: { amount, source } });

// ── Reviews ──

export interface ReviewItem {
  id: number;
  lessonId: string;
  questionIndex: number;
}

export const fetchDueReviews = (
  token: string,
): Promise<{ reviews: ReviewItem[]; count: number }> => apiFetch(path("/reviews/due"), { token });

export const answerReview = (
  token: string,
  reviewId: number,
  correct: boolean,
): Promise<{ nextReviewDate: string; intervalDays: number }> =>
  apiFetch(path(`/reviews/${reviewId}/answer`), { method: "POST", token, body: { correct } });

export const seedReviews = (token: string, lessonId: string): Promise<{ seeded: number }> =>
  apiFetch(path("/reviews/seed"), { method: "POST", token, body: { lessonId } });

// ── Badges ──

export const fetchBadges = (
  token: string,
): Promise<{ badges: { badgeKey: string; earnedAt: string }[] }> =>
  apiFetch(path("/badges"), { token });

// ── Leaderboard ──

export const fetchWeeklyLeaderboard = (
  token: string,
): Promise<{
  players: { username: string; weeklyXp: number; rank: number }[];
  yourRank: number | null;
  yourXp: number;
}> => apiFetch(path("/leaderboard/weekly"), { token });

// ── Module Exam ──

export const completeModuleExam = (
  token: string,
  moduleId: string,
  lessonIds: string[],
  score: number,
  passed: boolean,
): Promise<{ status: string; moduleId: string; passed: boolean }> =>
  apiFetch(path(`/modules/${moduleId}/exam`), {
    method: "POST",
    token,
    body: { lessonIds, score, passed },
  });
